import React, { useState, useEffect, useRef } from "react";
import { Play, Pause, ChevronDown, Check, Mic, Volume2, User, Sliders } from "lucide-react";
import Dropdown from "../Dropdown/Dropdown.js";
import ApiService from "../../services/Api.service.js";
import "./AudioConfigTab.css";

export default function AudioConfigTab({
  selectedAgent,
  handleInputChange,
  languageOptions = [],
  voiceCharacters = [],
  playingVoiceId,
  handlePlayVoice,
  sttOptions = [],
  sttModelOptions = [],
  ttsOptions = [],
  formErrors = {},
}) {
  const [voiceDropdownOpen, setVoiceDropdownOpen] = useState(false);
  const voiceDropdownRef = useRef(null);
  const [providerVoiceSettings, setProviderVoiceSettings] = useState({});
  const [showAdvancedTts, setShowAdvancedTts] = useState(true);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (voiceDropdownRef.current && !voiceDropdownRef.current.contains(e.target)) {
        setVoiceDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const currentTtsProvider = selectedAgent?.tts_type || "deepgram";
  const rawGender = selectedAgent?.gender || selectedAgent?.voice_gender;
  const currentGender = (rawGender === "male" || rawGender === "female") ? rawGender : "female";

  useEffect(() => {
    if (!currentTtsProvider) return;

    const fetchDefaultSettings = async () => {
      try {
        const res = await ApiService.getDefaultVoiceSetting(currentTtsProvider);
        if (res.status === 200 && res.data && !res.data.message) {
          setProviderVoiceSettings(res.data);

          const freshProviderSettings = {};
          const currentTtsSettings = selectedAgent?.tts_settings || {};

          Object.keys(res.data).forEach((key) => {
            freshProviderSettings[key] = currentTtsSettings[key] !== undefined
              ? currentTtsSettings[key]
              : res.data[key];
          });

          handleInputChange("tts_settings", freshProviderSettings);
        } else {
          setProviderVoiceSettings({});
          handleInputChange("tts_settings", {});
        }
      } catch (error) {
        console.error("Error fetching default TTS voice settings:", error);
        setProviderVoiceSettings({});
        handleInputChange("tts_settings", {});
      }
    };

    fetchDefaultSettings();
  }, [currentTtsProvider]);

  // Filter voice characters based on Language, TTS Provider AND selected Gender
  const primaryFiltered = voiceCharacters.filter(v => {
    const vGender = String(v.gender || "").toLowerCase();
    const genderMatch = !currentGender || currentGender === "all" || !vGender || vGender === currentGender;

    const vProvider = String(v.tts_type || "").toLowerCase();
    const curProvider = String(currentTtsProvider || "").toLowerCase();
    const providerMatch = !v.tts_type || vProvider === curProvider || vProvider === "all";

    const agentLang = String(selectedAgent?.language || "").toLowerCase();
    const vLang = String(v.language || "").toLowerCase();
    const langMatch = !selectedAgent?.language || !v.language || 
      vLang === agentLang || 
      vLang.includes(agentLang) || 
      agentLang.includes(vLang) ||
      (agentLang.startsWith("en") && vLang.startsWith("en")) ||
      (agentLang.startsWith("hi") && vLang.startsWith("hi"));

    return genderMatch && providerMatch && langMatch;
  });

  const secondaryFiltered = primaryFiltered.length > 0 ? primaryFiltered : voiceCharacters.filter(v => {
    const vGender = String(v.gender || "").toLowerCase();
    const genderMatch = !currentGender || currentGender === "all" || !vGender || vGender === currentGender;
    const vProvider = String(v.tts_type || "").toLowerCase();
    const curProvider = String(currentTtsProvider || "").toLowerCase();
    const providerMatch = !v.tts_type || vProvider === curProvider || vProvider === "all";
    return genderMatch && providerMatch;
  });

  const filteredVoices = secondaryFiltered.length > 0 ? secondaryFiltered : voiceCharacters.filter(v => {
    const vGender = String(v.gender || "").toLowerCase();
    return !currentGender || currentGender === "all" || !vGender || vGender === currentGender;
  });

  const selectedVoice = filteredVoices.find(v => v.id === selectedAgent?.voice_character_id) || filteredVoices[0];

  useEffect(() => {
    if (filteredVoices.length > 0) {
      const exists = filteredVoices.some(v => v.id === selectedAgent?.voice_character_id);
      if (!exists) {
        handleInputChange("voice_character_id", filteredVoices[0].id);
        if (filteredVoices[0].gender) {
          handleInputChange("voice_gender", filteredVoices[0].gender);
        }
      }
    } else {
      if (selectedAgent?.voice_character_id) {
        handleInputChange("voice_character_id", "");
      }
    }
  }, [currentGender, selectedAgent?.language, selectedAgent?.tts_type, voiceCharacters]);

  const formattedSttModelOptions = React.useMemo(() => {
    return (sttModelOptions || []).map(opt =>
      typeof opt === "string"
        ? { label: opt, value: opt }
        : { label: opt.label || opt.name || opt.value, value: opt.value || opt.id }
    );
  }, [sttModelOptions]);

  // Sync STT model if current model is not present in backend returned STT model options
  useEffect(() => {
    if (formattedSttModelOptions.length > 0) {
      const currentModel = selectedAgent?.stt_model || selectedAgent?.stt_settings?.model;
      const exists = formattedSttModelOptions.some(m => String(m.value) === String(currentModel));
      if (!exists || !currentModel) {
        const defaultModel = formattedSttModelOptions[0].value;
        handleInputChange("stt_model", defaultModel);
        handleInputChange("stt_settings", {
          ...(selectedAgent?.stt_settings || {}),
          model: defaultModel
        });
      }
    }
  }, [formattedSttModelOptions, selectedAgent?.stt_type]);

  return (
    <div className="tab-panel-content">
      {/* 1. Speech Recognition (STT) Section */}
      <div className="config-section-simple" style={{ marginBottom: 30 }}>
        <div className="config-group-title" style={{ display: "flex", alignItems: "center", gap: "8px", borderBottom: "1px solid var(--gray-100, #f1f5f9)", paddingBottom: "10px", marginBottom: "18px" }}>
          <Mic size={16} style={{ color: "var(--primary, #1e53b8)" }} />
          <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--slate-900)" }}>Speech Recognition</span>
        </div>
        
        {/* Spoken Language */}
        <div style={{ marginBottom: 20 }}>
          <label className="field-label" style={{ display: "block", marginBottom: "6px" }}>
            Spoken Language <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
          </label>
          <Dropdown
            options={languageOptions.map(lang => {
              const isString = typeof lang === "string";
              const val = isString ? lang : lang.value || lang.id || "";
              const lbl = isString ? lang : lang.label || lang.value;
              return { label: lbl, value: val };
            })}
            value={selectedAgent?.language || "hi"}
            onChange={(e) => handleInputChange("language", e.target.value)}
            placeholder="Select Spoken Language"
          />
          {formErrors.language && (
            <div style={{ color: "#ef4444", fontSize: "12px", fontWeight: 600, marginTop: "4px" }}>
              {formErrors.language}
            </div>
          )}
        </div>

        {/* Select STT Provider */}
        <div style={{ marginBottom: 20 }}>
          <label className="field-label" style={{ display: "block", marginBottom: "6px" }}>
            STT Provider Engine <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
          </label>
          <Dropdown
            options={sttOptions.map(opt => typeof opt === "string" ? { label: opt, value: opt } : { label: opt.label || opt.value, value: opt.value || opt.id })}
            value={selectedAgent?.stt_type || ""}
            onChange={(e) => {
              const newStt = e.target.value;
              handleInputChange("stt_type", newStt);
              handleInputChange("stt_model", "");
              handleInputChange("stt_settings", {
                ...(selectedAgent?.stt_settings || {}),
                model: ""
              });
            }}
            placeholder="Select STT Provider"
          />
          {formErrors.stt_type && (
            <div style={{ color: "#ef4444", fontSize: "12px", fontWeight: 600, marginTop: "4px" }}>
              {formErrors.stt_type}
            </div>
          )}
        </div>

        {/* STT Model & Silence Endpointing */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "20px" }}>
          <div>
            <label className="field-label" style={{ display: "block", marginBottom: "6px" }}>
              STT Model <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
            </label>
            <Dropdown
              options={formattedSttModelOptions}
              value={selectedAgent?.stt_model || selectedAgent?.stt_settings?.model || ""}
              onChange={(e) => {
                const val = e.target.value;
                handleInputChange("stt_model", val);
                handleInputChange("stt_settings", {
                  ...(selectedAgent?.stt_settings || {}),
                  model: val
                });
              }}
              placeholder="Select STT Model"
            />
            {formErrors.stt_model && (
              <div style={{ color: "#ef4444", fontSize: "12px", fontWeight: 600, marginTop: "4px" }}>
                {formErrors.stt_model}
              </div>
            )}
          </div>

          <div className="slider-group" style={{ marginBottom: 0 }}>
            <div className="slider-header">
              <span className="field-label">
                Silence Endpointing <span style={{ color: "#94a3b8", fontWeight: "normal", fontSize: "12px", marginLeft: "4px" }}>(Optional)</span>
              </span>
              <span className="slider-val">{selectedAgent?.endpointing ?? 100} ms</span>
            </div>
            <input
              type="range"
              min={50}
              max={1000}
              step={10}
              className="custom-range-slider"
              value={selectedAgent?.endpointing ?? 100}
              onChange={(e) => handleInputChange("endpointing", Number(e.target.value))}
            />
            <div className="field-helper">
              Milliseconds of user silence required before concluding user turn.
            </div>
          </div>
        </div>
      </div>

      {/* 2. Text to Speech (TTS) Section */}
      <div className="config-section-simple">
        <div className="config-group-title" style={{ display: "flex", alignItems: "center", gap: "8px", borderBottom: "1px solid var(--gray-100, #f1f5f9)", paddingBottom: "10px", marginBottom: "18px" }}>
          <Volume2 size={16} style={{ color: "var(--primary, #1e53b8)" }} />
          <span style={{ fontSize: "14px", fontWeight :"700", color: "var(--slate-900)" }}>Text to Speech</span>
        </div>

        {/* Select TTS Provider */}
        <div style={{ marginBottom: 20 }}>
          <label className="field-label" style={{ display: "block", marginBottom: "6px" }}>
            TTS Provider Engine <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
          </label>
          <Dropdown
            options={ttsOptions.map(opt => typeof opt === "string" ? { label: opt, value: opt } : { label: opt.label || opt.value, value: opt.value || opt.id })}
            value={currentTtsProvider}
            onChange={(e) => handleInputChange("tts_type", e.target.value)}
            placeholder="Select TTS Provider"
          />
        </div>

        {/* Select Voice Synthesis Avatar */}
        <div style={{ marginBottom: 20 }}>
          <label className="field-label" style={{ display: "block", marginBottom: "6px" }}>
            Voice Character / Model <span style={{ color: "#94a3b8", fontWeight: "normal", fontSize: "12px", marginLeft: "4px" }}>(Optional)</span>
          </label>
          <div className="voice-dd-container" ref={voiceDropdownRef} style={{ position: "relative", width: "100%", boxSizing: "border-box" }}>
            <div 
              className={`voice-dd-trigger ${voiceDropdownOpen ? "open" : ""}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                minHeight: "56px",
                padding: "8px 16px",
                background: "var(--white, #ffffff)",
                border: voiceDropdownOpen ? "1px solid var(--primary, #1e53b8)" : "1px solid var(--gray-200, #e2e8f0)",
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                position: "relative",
                boxShadow: voiceDropdownOpen ? "0 0 0 3px rgba(30, 83, 184, 0.1)" : "none",
                boxSizing: "border-box"
              }}
              onClick={() => setVoiceDropdownOpen(!voiceDropdownOpen)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div 
                  style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center", 
                    width: "40px", 
                    height: "40px", 
                    borderRadius: "50%", 
                    background: selectedVoice 
                      ? (selectedVoice.gender === "female" 
                          ? "linear-gradient(135deg, #fff1f2, #fce7f3)" 
                          : "linear-gradient(135deg, #eff6ff, #dbeafe)")
                      : "var(--gray-50, #f8fafc)",
                    border: selectedVoice
                      ? (selectedVoice.gender === "female"
                          ? "1px solid #fecdd3"
                          : "1px solid #bfdbfe")
                      : "1px solid var(--gray-200, #e2e8f0)",
                    fontSize: "20px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                  }}
                >
                  {selectedVoice ? (
                    <User size={20} style={{ color: selectedVoice.gender === "female" ? "#e63946" : "#1e53b8" }} />
                  ) : (
                    <User size={20} style={{ color: "var(--slate-400, #94a3b8)" }} />
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
                  <span style={{ fontWeight: 600, color: "var(--slate-800)", fontSize: "14px" }}>
                    {selectedVoice ? selectedVoice.name : "Select a Voice"}
                  </span>
                  <span style={{ fontSize: "12px", color: "var(--slate-500)", fontWeight: 500 }}>
                    {selectedVoice ? `${selectedVoice.gender === "female" ? "Female" : "Male"} • ${selectedVoice.description || "Neural voice"}` : "Choose voice character"}
                  </span>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "12px" }} onClick={(e) => e.stopPropagation()}>
                {selectedVoice && (
                  <button
                    type="button"
                    className={`btn-play-voice ${playingVoiceId === selectedVoice.id ? "playing" : ""}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      border: "none",
                      background: "rgba(26, 54, 93, 0.05)",
                      color: "var(--primary, #1e53b8)",
                      cursor: "pointer",
                      transition: "all 0.2s ease"
                    }}
                    onClick={(e) => handlePlayVoice(e, selectedVoice)}
                    title="Play voice preview"
                  >
                    {playingVoiceId === selectedVoice.id ? <Pause size={14} /> : <Play size={14} />}
                  </button>
                )}
                <ChevronDown 
                  size={18} 
                  style={{ 
                    color: "var(--slate-400)", 
                    transform: voiceDropdownOpen ? "rotate(180deg)" : "none",
                    transition: "transform 0.2s ease",
                    cursor: "pointer"
                  }} 
                  onClick={() => setVoiceDropdownOpen(!voiceDropdownOpen)}
                />
              </div>
            </div>

            {/* Dropdown Options List */}
            {voiceDropdownOpen && (
              <div 
                className="voice-dd-menu"
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  left: 0,
                  width: "100%",
                  background: "var(--white, #ffffff)",
                  border: "1px solid var(--gray-200, #e2e8f0)",
                  borderRadius: "12px",
                  boxShadow: "0 10px 25px rgba(0, 0, 0, 0.08)",
                  zIndex: 2050,
                  maxHeight: "280px",
                  overflowY: "auto",
                  padding: "6px",
                  boxSizing: "border-box"
                }}
              >
                {filteredVoices.map(char => (
                  <div
                    key={char.id}
                    className={`voice-dd-option ${selectedAgent?.voice_character_id === char.id ? "selected" : ""}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      marginBottom: "4px",
                      background: selectedAgent?.voice_character_id === char.id ? "rgba(26, 54, 93, 0.04)" : "transparent"
                    }}
                    onClick={() => {
                      handleInputChange("voice_character_id", char.id);
                      if (char.gender) {
                        handleInputChange("voice_gender", char.gender);
                      }
                      setVoiceDropdownOpen(false);
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div 
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "center", 
                          width: "36px", 
                          height: "36px", 
                          borderRadius: "50%", 
                          background: char.gender === "female" 
                            ? "linear-gradient(135deg, #fff1f2, #fce7f3)" 
                            : "linear-gradient(135deg, #eff6ff, #dbeafe)",
                          border: char.gender === "female"
                            ? "1px solid #fecdd3"
                            : "1px solid #bfdbfe",
                          fontSize: "18px"
                        }}
                      >
                        <User size={18} style={{ color: char.gender === "female" ? "#e63946" : "#1e53b8" }} />
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span style={{ fontWeight: 600, color: "var(--slate-800)", fontSize: "13px" }}>
                            {char.name}
                          </span>
                          <span 
                            style={{ 
                              fontSize: "10px", 
                              fontWeight: 600, 
                              textTransform: "uppercase", 
                              color: "var(--slate-400)",
                              background: "var(--gray-100, #f1f5f9)",
                              padding: "1px 6px",
                              borderRadius: "4px"
                            }}
                          >
                            {char.gender}
                          </span>
                        </div>
                        <span style={{ fontSize: "11px", color: "var(--slate-500)" }}>
                          {char.description}
                        </span>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className={`btn-play-voice ${playingVoiceId === char.id ? "playing" : ""}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "28px",
                          height: "28px",
                          borderRadius: "50%",
                          border: "none",
                          background: playingVoiceId === char.id ? "var(--primary, #1e53b8)" : "rgba(26, 54, 93, 0.05)",
                          color: playingVoiceId === char.id ? "var(--white, #ffffff)" : "var(--primary, #1e53b8)",
                          cursor: "pointer",
                          transition: "all 0.2s ease"
                        }}
                        onClick={(e) => handlePlayVoice(e, char)}
                        title="Play voice preview"
                      >
                        {playingVoiceId === char.id ? <Pause size={12} /> : <Play size={12} />}
                      </button>
                      
                      {selectedAgent?.voice_character_id === char.id && (
                        <Check size={16} style={{ color: "var(--primary, #1e53b8)", marginLeft: "4px" }} />
                      )}
                    </div>
                  </div>
                ))}

                {filteredVoices.length === 0 && (
                  <div style={{ padding: "16px", textAlign: "center", color: "var(--slate-400)", fontSize: "13px" }}>
                    No voices found matching Spoken Language ({selectedAgent?.language}) and TTS Provider ({currentTtsProvider}).
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Speech Speed Slider */}
        <div className="slider-group" style={{ marginTop: 18, marginBottom: 0 }}>
          <div className="slider-header">
            <span className="field-label">TTS Speech Speed Rate</span>
            <span className="slider-val">{(selectedAgent?.speech_speed ?? 1.0).toFixed(1)}x</span>
          </div>
          <input
            type="range"
            min={0.5}
            max={2.0}
            step={0.1}
            className="custom-range-slider"
            value={selectedAgent?.speech_speed ?? 1.0}
            onChange={(e) => handleInputChange("speech_speed", Number(e.target.value))}
          />
          <div className="field-helper">
            Adjusts the playback rate of synthesized voice output during calls.
          </div>
        </div>

        {/* Advanced TTS Settings according to Provider */}
        <div style={{ marginTop: 22, borderTop: "1px solid var(--gray-200, #e2e8f0)", paddingTop: 18 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Sliders size={16} style={{ color: "var(--primary, #1e53b8)" }} />
              <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--slate-900)" }}>
                Advanced TTS Settings ({currentTtsProvider})
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowAdvancedTts(!showAdvancedTts)}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--primary, #1e53b8)",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                padding: "2px 8px"
              }}
            >
              {showAdvancedTts ? "Hide" : "Show"} Advanced Settings
            </button>
          </div>

          {showAdvancedTts && (
            <div
              style={{
                marginTop: 10,
                padding: "16px",
                borderRadius: "10px",
                background: "var(--slate-50, #f8fafc)",
                border: "1px solid var(--slate-200, #e2e8f0)",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "16px"
              }}
            >
              {(() => {
                const keys = Object.keys(providerVoiceSettings);

                if (keys.length === 0) {
                  return (
                    <div style={{ gridColumn: "1 / -1", textAlign: "center", fontSize: "13px", color: "var(--slate-500)", margin: "8px 0" }}>
                      No advanced TTS settings available for {currentTtsProvider}.
                    </div>
                  );
                }

                return keys.map((key) => {
                  const defaultVal = providerVoiceSettings[key];
                  const currentVal = selectedAgent?.tts_settings?.[key] !== undefined
                    ? selectedAgent.tts_settings[key]
                    : defaultVal;

                  const isBool = typeof defaultVal === "boolean" || typeof currentVal === "boolean";
                  const isNum = typeof defaultVal === "number" || typeof currentVal === "number";

                  const handleSettingUpdate = (newVal) => {
                    const updated = {
                      ...(selectedAgent?.tts_settings || providerVoiceSettings),
                      [key]: newVal
                    };
                    handleInputChange("tts_settings", updated);
                  };

                  return (
                    <div key={key} style={{ display: "flex", flexDirection: "column" }}>
                      <label
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "var(--slate-600)",
                          textTransform: "capitalize",
                          marginBottom: "6px"
                        }}
                      >
                        {key.replace(/_/g, " ")}
                      </label>
                      {isBool ? (
                        <select
                          value={currentVal ? "true" : "false"}
                          onChange={(e) => handleSettingUpdate(e.target.value === "true")}
                          className="field-input-textarea"
                          style={{ height: "38px", padding: "0 12px", fontSize: "13px", cursor: "pointer" }}
                        >
                          <option value="true">True</option>
                          <option value="false">False</option>
                        </select>
                      ) : (
                        <input
                          type={isNum ? "number" : "text"}
                          step={isNum ? "any" : undefined}
                          value={currentVal ?? ""}
                          onChange={(e) => {
                            const rawVal = e.target.value;
                            handleSettingUpdate(isNum ? (rawVal === "" ? "" : Number(rawVal)) : rawVal);
                          }}
                          className="field-input-textarea"
                          style={{ height: "38px", padding: "0 12px", fontSize: "13px" }}
                        />
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
